import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PTSWizardPanel = ({ selectedSymbol }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [algorithmActive, setAlgorithmActive] = useState(true);
  const [signals, setSignals] = useState([]);
  const [parameters, setParameters] = useState({
    // PTS-WIZARD-V3 default parameters
    sma_short: 10,
    sma_long: 21,
    price_source: 'hlc3',
    rsi_period: 14,
    rsi_overbought: 70,
    rsi_oversold: 30,
    macd_fast: 12,
    macd_slow: 26,
    macd_signal: 9,
    bb_period: 20,
    bb_multiplier: 2,
    fibonacci_levels: [0.236, 0.382, 0.5, 0.618, 0.764],
    volume_threshold: 1.5,
    trend_strength: 0.75,
    support_resistance_period: 50
  });

  // Mock algorithm signals
  const mockSignals = [
    {
      id: 1,
      type: 'BUY',
      strength: 'STRONG',
      price: 43275.50,
      timestamp: new Date(Date.now() - 300000),
      confidence: 87,
      indicators: ['RSI Oversold', 'MACD Bullish Cross', 'Support Level']
    },
    {
      id: 2,
      type: 'SELL',
      strength: 'MODERATE',
      price: 43180.25,
      timestamp: new Date(Date.now() - 900000),
      confidence: 72,
      indicators: ['Fibonacci Resistance', 'Volume Spike']
    },
    {
      id: 3,
      type: 'HOLD',
      strength: 'WEAK',
      price: 43225.75,
      timestamp: new Date(Date.now() - 1800000),
      confidence: 45,
      indicators: ['Sideways Trend', 'Low Volume']
    }
  ];

  useEffect(() => {
    setSignals(mockSignals);
  }, [selectedSymbol]);

  const handleParameterChange = (key, value) => {
    setParameters(prev => ({
      ...prev,
      [key]: parseFloat(value) || value
    }));
  };

  const getSignalColor = (type, strength) => {
    if (type === 'BUY') return strength === 'STRONG' ? 'text-success' : 'text-accent';
    if (type === 'SELL') return strength === 'STRONG' ? 'text-error' : 'text-warning';
    return 'text-muted-foreground';
  };

  const getSignalIcon = (type) => {
    switch (type) {
      case 'BUY': return 'TrendingUp';
      case 'SELL': return 'TrendingDown';
      default: return 'Minus';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 h-full">
      {/* Panel Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
            <Icon name="Zap" size={16} color="var(--color-accent-foreground)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">PTS-WIZARD-V3</h3>
            <p className="text-sm text-muted-foreground">Advanced Trading Algorithm</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${algorithmActive ? 'bg-success' : 'bg-error'}`} />
          <span className="text-sm text-muted-foreground">
            {algorithmActive ? 'Active' : 'Inactive'}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
          </Button>
        </div>
      </div>
      {/* Algorithm Status */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-muted rounded-lg p-3">
          <div className="text-xs text-muted-foreground mb-1">Confidence</div>
          <div className="text-lg font-semibold text-foreground">87%</div>
        </div>
        <div className="bg-muted rounded-lg p-3">
          <div className="text-xs text-muted-foreground mb-1">Signals Today</div>
          <div className="text-lg font-semibold text-foreground">24</div>
        </div>
        <div className="bg-muted rounded-lg p-3">
          <div className="text-xs text-muted-foreground mb-1">Success Rate</div>
          <div className="text-lg font-semibold text-success">73.5%</div>
        </div>
        <div className="bg-muted rounded-lg p-3">
          <div className="text-xs text-muted-foreground mb-1">Risk Level</div>
          <div className="text-lg font-semibold text-warning">Medium</div>
        </div>
      </div>
      {/* Recent Signals */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Recent Signals</h4>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {signals?.map((signal) => (
            <div key={signal?.id} className="bg-muted rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon 
                    name={getSignalIcon(signal?.type)} 
                    size={16} 
                    className={getSignalColor(signal?.type, signal?.strength)}
                  />
                  <span className={`text-sm font-medium ${getSignalColor(signal?.type, signal?.strength)}`}>
                    {signal?.type} - {signal?.strength}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {signal?.timestamp?.toLocaleTimeString()}
                </div>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-foreground font-medium">
                  ${signal?.price?.toLocaleString()}
                </span>
                <span className="text-xs text-muted-foreground">
                  {signal?.confidence}% confidence
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {signal?.indicators?.map((indicator, index) => (
                  <span 
                    key={index}
                    className="text-xs bg-background px-2 py-1 rounded text-muted-foreground"
                  >
                    {indicator}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Expandable Parameters Section */}
      {isExpanded && (
        <div className="border-t border-border pt-4">
          <h4 className="text-sm font-medium text-foreground mb-3">Algorithm Parameters</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-64 overflow-y-auto">
            <Input
              label="SMA Short Period"
              type="number"
              value={parameters?.sma_short}
              onChange={(e) => handleParameterChange('sma_short', e?.target?.value)}
              className="text-sm"
            />
            <Input
              label="SMA Long Period"
              type="number"
              value={parameters?.sma_long}
              onChange={(e) => handleParameterChange('sma_long', e?.target?.value)}
              className="text-sm"
            />
            <Input
              label="RSI Period"
              type="number"
              value={parameters?.rsi_period}
              onChange={(e) => handleParameterChange('rsi_period', e?.target?.value)}
              className="text-sm"
            />
            <Input
              label="RSI Overbought"
              type="number"
              value={parameters?.rsi_overbought}
              onChange={(e) => handleParameterChange('rsi_overbought', e?.target?.value)}
              className="text-sm"
            />
            <Input
              label="RSI Oversold"
              type="number"
              value={parameters?.rsi_oversold}
              onChange={(e) => handleParameterChange('rsi_oversold', e?.target?.value)}
              className="text-sm"
            />
            <Input
              label="MACD Fast"
              type="number"
              value={parameters?.macd_fast}
              onChange={(e) => handleParameterChange('macd_fast', e?.target?.value)}
              className="text-sm"
            />
            <Input
              label="MACD Slow"
              type="number"
              value={parameters?.macd_slow}
              onChange={(e) => handleParameterChange('macd_slow', e?.target?.value)}
              className="text-sm"
            />
            <Input
              label="Bollinger Bands Period"
              type="number"
              value={parameters?.bb_period}
              onChange={(e) => handleParameterChange('bb_period', e?.target?.value)}
              className="text-sm"
            />
          </div>
          
          <div className="flex items-center gap-2 mt-4">
            <Button
              variant="default"
              size="sm"
              onClick={() => setAlgorithmActive(!algorithmActive)}
            >
              <Icon name={algorithmActive ? 'Pause' : 'Play'} size={16} />
              {algorithmActive ? 'Pause' : 'Start'} Algorithm
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="RotateCcw" size={16} />
              Reset Parameters
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="Save" size={16} />
              Save Config
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PTSWizardPanel;